import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectGroup, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertTriangle, CheckCircle, Wallet, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

import { API, errorText, newIdempotencyKey } from '@/utils/api';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const FY_MONTHS = [
  { value: '4', label: 'Apr' }, { value: '5', label: 'May' }, { value: '6', label: 'Jun' },
  { value: '7', label: 'Jul' }, { value: '8', label: 'Aug' }, { value: '9', label: 'Sep' },
  { value: '10', label: 'Oct' }, { value: '11', label: 'Nov' }, { value: '12', label: 'Dec' },
  { value: '1', label: 'Jan' }, { value: '2', label: 'Feb' }, { value: '3', label: 'Mar' },
];
const getCurrentFY = () => {
  const now = new Date();
  return now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
};

export default function Payments() {
  const { user, selectedDivision } = useOutletContext();
  const [loading, setLoading] = useState(true);
  const [ageingData, setAgeingData] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [savingPayment, setSavingPayment] = useState(false);
  const paymentKeyRef = useRef(newIdempotencyKey());
  
  // Payment Summary State
  const [summaryYear, setSummaryYear] = useState(getCurrentFY());
  const [summaryMonth, setSummaryMonth] = useState('all');
  const [summaryRegion, setSummaryRegion] = useState('all');
  const [summaryCustomer, setSummaryCustomer] = useState('all');
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [showSummary, setShowSummary] = useState(true);
  const [showAllPayments, setShowAllPayments] = useState(false);
  const [viewMode, setViewMode] = useState('bulk'); // 'bulk', 'grouped', or 'list'
  
  const [paymentForm, setPaymentForm] = useState({
    payments: [
      {
        payment_amount: '',
        payment_date: new Date().toISOString().split('T')[0],
        payment_mode: 'neft',
        payment_reference: '',
        remarks: ''
      }
    ],
    cd_policy: 'no_cd',
    close_cd_after_management_approval: false
  });

  const [selectedCredits, setSelectedCredits] = useState([]);
  const [availableCredits, setAvailableCredits] = useState([]);
  
  const fetchPaymentSummary = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/analytics/payments-summary`, {
        params: {
          year: summaryYear,
          month: summaryMonth !== 'all' ? parseInt(summaryMonth) : undefined,
          region: summaryRegion !== 'all' ? summaryRegion : undefined,
          customer_id: summaryCustomer !== 'all' ? summaryCustomer : undefined,
          division: selectedDivision !== 'all' ? selectedDivision : undefined
        },
        withCredentials: true
      });
      setPaymentSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch payment summary:', error);
      toast.error('Could not load the collections summary — the figures shown may be stale.');
    }
  }, [summaryYear, summaryMonth, summaryRegion, summaryCustomer, selectedDivision]);
  
  const fetchAgeingReport = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/analytics/ageing`, {
        params: {
          division: selectedDivision !== 'all' ? selectedDivision : undefined
        },
        withCredentials: true
      });
      setAgeingData(response.data);
    } catch (error) {
      console.error('Failed to fetch ageing report:', error);
      toast.error('Failed to load payment tracking data');
    } finally {
      setLoading(false);
    }
  }, [selectedDivision]);
  
  useEffect(() => {
    fetchAgeingReport();
  }, [fetchAgeingReport]);

  // fetchPaymentSummary already re-runs on every summary-filter change via its
  // own useCallback deps; a single effect avoids the duplicate concurrent fetch
  // that the previous two effects fired on mount and on each filter change.
  useEffect(() => {
    fetchPaymentSummary();
  }, [fetchPaymentSummary]);

  const handleRecordPayment = async () => {
    if (!selectedInvoice || savingPayment) return;
    setSavingPayment(true);
    
    try {
      const response = await axios.post(
        `${API}/sales/record-payment`,
        null,
        { 
          params: {
            transaction_id: selectedInvoice.transaction_id,
            payments: JSON.stringify(paymentForm.payments),
            credit_ids: JSON.stringify(selectedCredits.map(c => c.transaction_id)),
            cd_policy: paymentForm.cd_policy,
            close_cd_after_management_approval: paymentForm.close_cd_after_management_approval,
            idempotency_key: paymentKeyRef.current
          },
          withCredentials: true 
        }
      );
      
      const result = response.data;
      
      const paidAmount = result.paid_amount || 0;

      let message = 'Payment recorded\n';
      message += `Invoice Total: ₹${result.invoice_amount.toLocaleString()}\n`;
      message += `Paid so far: ₹${paidAmount.toLocaleString()}\n`;
      if (result.cash_discount_amount > 0) {
        message += `CD Amount: ₹${result.cash_discount_amount.toLocaleString()}\n`;
      }
      message += `Status: ${result.payment_status}`;
      
      toast.success(message);
      paymentKeyRef.current = newIdempotencyKey();
      setPaymentDialogOpen(false);
      setSelectedInvoice(null);
      setSelectedCustomer(null);
      setPaymentForm({
        payments: [
          {
            payment_amount: '',
            payment_date: new Date().toISOString().split('T')[0],
            payment_mode: 'neft',
            payment_reference: '',
            remarks: ''
          }
        ],
        cd_policy: 'no_cd',
        close_cd_after_management_approval: false
      });
      
      // Refresh data
      fetchAgeingReport();
    } catch (error) {
      console.error('Failed to record payment:', error);
      toast.error(errorText(error) || 'Failed to record payment');
    } finally {
      setSavingPayment(false);
    }
  };
  
  const fetchCustomerDetails = async (customerId) => {
    try {
      const response = await axios.get(`${API}/customers`, {
        withCredentials: true
      });
      const customer = response.data.find(c => c.customer_id === customerId);
      setSelectedCustomer(customer);
    } catch (error) {
      console.error('Failed to fetch customer:', error);
    }
  };
  
  const fetchAvailableCredits = async (customerId) => {
    try {
      const resp = await axios.get(`${API}/customers/${customerId}/available-credits`, {
        withCredentials: true
      });
      return resp.data || [];
    } catch (error) {
      console.error('Failed to fetch credits:', error);
      return [];
    }
  };

  const openPaymentDialog = async (item) => {
    setSelectedInvoice(item);
    paymentKeyRef.current = newIdempotencyKey();
    setPaymentDialogOpen(true);
    setSelectedCredits([]);

    setPaymentForm((prev) => ({
      ...prev,
      payments: [
        {
          payment_amount: '',
          payment_date: new Date().toISOString().split('T')[0],
          payment_mode: 'neft',
          payment_reference: '',
          remarks: ''
        }
      ],
      close_cd_after_management_approval: false
    }));

    if (item?.customer_id) {
      const list = await fetchAvailableCredits(item.customer_id);
      setAvailableCredits(list);
      fetchCustomerByName(item.customer_name);
    }
  };
  
  const fetchCustomerByName = async (customerName) => {
    try {
      const response = await axios.get(`${API}/customers`, {
        withCredentials: true
      });
      const customer = response.data.find(c => c.name === customerName);
      setSelectedCustomer(customer);
    } catch (error) {
      console.error('Failed to fetch customer:', error);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!ageingData) {
    return (
      <div className="p-8">
        <p className="text-muted-foreground">No data available</p>
      </div>
    );
  }
  
  const getSeverityColor = (bucket) => {
    const colors = {
      '0-15': 'bg-green-100 text-green-800',
      '16-45': 'bg-blue-100 text-blue-800',
      '46-90': 'bg-yellow-100 text-yellow-800',
      '91-120': 'bg-orange-100 text-orange-800',
      '121+': 'bg-red-100 text-red-800'
    };
    return colors[bucket] || 'bg-gray-100 text-gray-800';
  };
  
  const getBucketLabel = (bucket) => {
    const labels = {
      '0-15': '0-15 Days (Within Credit)',
      '16-45': '16-45 Days (Within Credit)',
      '46-90': '46-90 Days (Overdue)',
      '91-120': '91-120 Days (Overdue)',
      '121+': '121+ Days (Overdue)'
    };
    return labels[bucket] || bucket;
  };
  
  const renderAgeingTable = (data, bucket) => {
    if (data.length === 0) {
      return (
        <div className="text-center py-12">
          <p className="text-muted-foreground">No payments in this category</p>
        </div>
      );
    }
    
    const totalBalance = data.reduce((sum, item) => sum + item.amount, 0);
    const totalInterest = data.reduce((sum, item) => sum + (item.interest_amount || 0), 0);
    
    const canRecordPayment = user?.role === 'admin' || user?.role === 'accounts';
    
    return (
      <div>
        <div className="mb-4 p-4 bg-muted rounded-lg">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <span className="text-sm font-body text-muted-foreground">Total Balance Outstanding</span>
              <span className="block text-2xl font-heading font-bold text-foreground">
                ₹{totalBalance.toLocaleString()}
              </span>
            </div>
            <div>
              <span className="text-sm font-body text-muted-foreground">Total Invoices</span>
              <span className="block text-2xl font-heading font-bold text-foreground">
                {data.length}
              </span>
            </div>
            <div>
              <span className="text-sm font-body text-muted-foreground">Interest Accrued</span>
              <span className="block text-2xl font-heading font-bold text-amber-600">
                ₹{totalInterest.toLocaleString()}
              </span>
            </div>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left p-3 font-body text-sm font-medium text-muted-foreground">Customer</th>
                <th className="text-left p-3 font-body text-sm font-medium text-muted-foreground">Code</th>
                <th className="text-left p-3 font-body text-sm font-medium text-muted-foreground">Invoice</th>
                <th className="text-left p-3 font-body text-sm font-medium text-muted-foreground">Invoice Amt</th>
                <th className="text-left p-3 font-body text-sm font-medium text-muted-foreground">Balance</th>
                <th className="text-left p-3 font-body text-sm font-medium text-muted-foreground">Days</th>
                <th className="text-left p-3 font-body text-sm font-medium text-muted-foreground">Interest</th>
                <th className="text-left p-3 font-body text-sm font-medium text-muted-foreground">Region</th>
                {canRecordPayment && (
                  <th className="text-left p-3 font-body text-sm font-medium text-muted-foreground">Action</th>
                )}
              </tr>
            </thead>
            <tbody>
              {data.map((item, idx) => (
                <tr key={idx} className="border-b border-border/50 hover:bg-muted/50 transition-colors">
                  <td className="p-3 font-body text-foreground text-sm">
                    {item.customer_name}
                    {item.status && item.status !== 'active' && (
                      <span
                        className={`ml-1.5 px-1.5 py-0.5 rounded border text-[9px] font-semibold uppercase tracking-wide ${item.status === 'blocked' ? 'bg-red-100 text-red-800 border-red-300' : 'bg-amber-100 text-amber-800 border-amber-300'}`}
                        title={item.status_reason || ''}
                      >
                        {item.status}
                      </span>
                    )}
                  </td>
                  <td className="p-3 font-body text-muted-foreground text-sm">{item.customer_code || '-'}</td>
                  <td className="p-3 font-body text-foreground text-sm">
                    {item.invoice_number}
                    {item.entry_type && item.entry_type !== 'invoice' && (
                      <span className="inline-block ml-1.5 px-1.5 py-0.5 rounded border border-purple-300 bg-purple-100 text-purple-800 text-[9px] font-semibold leading-none whitespace-nowrap align-middle">
                        {item.entry_type.replace(/_/g, ' ')}
                      </span>
                    )}
                  </td>
                  <td className="p-3 font-body text-muted-foreground text-sm">₹{(item.invoice_amount || item.amount).toLocaleString()}</td>
                  <td className="p-3 font-body text-foreground font-semibold text-sm">
                    ₹{item.amount.toLocaleString()}
                    {item.paid_amount > 0 && (
                      <span className="block text-xs text-green-600">
                        (Paid: ₹{item.paid_amount.toLocaleString()})
                      </span>
                    )}
                  </td>
                  <td className="p-3">
                    <Badge className={getSeverityColor(bucket)}>
                      {item.days_from_invoice} days
                    </Badge>
                  </td>
                  <td className="p-3 font-body text-amber-600 font-semibold text-sm">
                    ₹{(item.interest_amount || 0).toLocaleString()}
                  </td>
                  <td className="p-3 font-body text-muted-foreground text-sm">{item.region}</td>
                  {canRecordPayment && (
                    <td className="p-3">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openPaymentDialog(item)}
                        className="text-xs"
                      >
                        Record Payment
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };
  
  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-heading font-bold text-foreground mb-1">
            Payment Tracking
          </h1>
          <p className="text-muted-foreground text-sm">Monitor payments received and ageing</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowSummary(!showSummary)}
        >
          {showSummary ? 'Hide Summary' : 'Show Summary'}
        </Button>
      </div>
      
      {/* Payments Received Summary */}
      {showSummary && paymentSummary && (
        <Card className="mb-6 border-border/60 shadow-sm bg-gradient-to-br from-green-50 to-white">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-heading font-semibold flex items-center gap-2">
              <Wallet className="text-green-600" size={20} />
              Payments Received Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Filters */}
            <div className="flex flex-wrap gap-3 mb-4">
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Year</label>
                <Select value={summaryYear.toString()} onValueChange={(v) => setSummaryYear(parseInt(v))}>
                  <SelectTrigger className="w-32 h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="2022">FY 2022-23</SelectItem>
                    <SelectItem value="2023">FY 2023-24</SelectItem>
                    <SelectItem value="2024">FY 2024-25</SelectItem>
                    <SelectItem value="2025">FY 2025-26</SelectItem>
                    <SelectItem value="2026">FY 2026-27</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Month</label>
                <Select value={summaryMonth} onValueChange={setSummaryMonth}>
                  <SelectTrigger className="w-28 h-8 text-sm">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Months</SelectItem>
                    {FY_MONTHS.map((m) => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {paymentSummary.regions?.length > 0 && (
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Region</label>
                  <Select value={summaryRegion} onValueChange={setSummaryRegion}>
                    <SelectTrigger className="w-32 h-8 text-sm">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Regions</SelectItem>
                      {paymentSummary.regions.map((r) => (
                        <SelectItem key={r} value={r}>{r}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {paymentSummary.customers?.length > 0 && (
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Customer</label>
                  <Select value={summaryCustomer} onValueChange={setSummaryCustomer}>
                    <SelectTrigger className="w-40 h-8 text-sm">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Customers</SelectItem>
                      {(() => {
                        const allC = [...paymentSummary.customers].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                        const groups = [
                          { label: 'A — F', test: n => n >= 'A' && n < 'G' },
                          { label: 'G — L', test: n => n >= 'G' && n < 'M' },
                          { label: 'M — R', test: n => n >= 'M' && n < 'S' },
                          { label: 'S — Z', test: n => n >= 'S' },
                        ];
                        return groups.map(g => {
                          const items = allC.filter(c => g.test((c.name || '')[0]?.toUpperCase()));
                          if (!items.length) return null;
                          return (
                            <SelectGroup key={g.label}>
                              <SelectLabel className="text-xs font-bold text-indigo-600">{g.label}</SelectLabel>
                              {items.map(c => (
                                <SelectItem key={c.customer_id} value={c.customer_id}>
                                  {c.name?.substring(0, 30) || 'Unknown'}
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          );
                        });
                      })()}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            
            {/* Summary Stats */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="bg-white p-3 rounded-lg border">
                <p className="text-xs text-muted-foreground">Total Amount Due</p>
                <p className="text-xl font-bold text-red-600">
                  ₹{((paymentSummary.total_amount_due || 0) / 100000).toFixed(2)}L
                </p>
              </div>
              <div className="bg-white p-3 rounded-lg border">
                <p className="text-xs text-muted-foreground">Transactions</p>
                <p className="text-xl font-bold text-foreground">
                  {paymentSummary.total_count}
                </p>
              </div>
            </div>

            {/* Amount Due by State/Region */}
            {paymentSummary.amount_due_region_chart?.length > 0 && (
              <div className="bg-white p-3 rounded-lg border mb-4">
                <p className="text-xs font-medium text-muted-foreground mb-2">Amount Due by State</p>
                <div className="space-y-2">
                  {paymentSummary.amount_due_region_chart.map((r, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground truncate mr-2">{r.name}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-24 bg-gray-100 rounded-full h-2">
                          <div
                            className="bg-red-500 h-2 rounded-full"
                            style={{ width: `${Math.min(100, (r.amount / (paymentSummary.amount_due_region_chart[0]?.amount || 1)) * 100)}%` }}
                          />
                        </div>
                        <span className="font-medium text-foreground whitespace-nowrap">₹{r.amount.toFixed(2)}L</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Monthly Chart */}
            {paymentSummary.monthly_chart && summaryMonth === 'all' && (
              <div className="bg-white p-3 rounded-lg border">
                <p className="text-xs font-medium text-muted-foreground mb-2">Monthly Breakdown</p>
                <ResponsiveContainer width="100%" height={150}>
                  <BarChart data={paymentSummary.monthly_chart}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} stroke="#64748b" />
                    <YAxis tick={{ fontSize: 10 }} stroke="#64748b" tickFormatter={(v) => `${v}L`} />
                    <Tooltip 
                      formatter={(value) => [`₹${value.toFixed(2)}L`, 'Received']}
                      contentStyle={{ fontSize: '12px' }}
                    />
                    <Bar isAnimationActive={false} dataKey="amount" fill="#22C55E" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
            
            {/* Payments List Section */}
            {paymentSummary.payments?.length > 0 && (
              <div className="mt-4">
                {/* Header with toggle buttons */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-foreground">
                      Payments Received ({paymentSummary.total_count})
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* View Mode Toggle */}
                    <div className="flex rounded-lg border overflow-hidden text-xs">
                      <button
                        className={`px-3 py-1.5 ${viewMode === 'bulk' ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80'}`}
                        onClick={() => setViewMode('bulk')}
                      >
                        Bulk Payments
                      </button>
                      <button
                        className={`px-3 py-1.5 ${viewMode === 'grouped' ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80'}`}
                        onClick={() => setViewMode('grouped')}
                      >
                        By Customer
                      </button>
                      <button
                        className={`px-3 py-1.5 ${viewMode === 'list' ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80'}`}
                        onClick={() => setViewMode('list')}
                      >
                        All Payments
                      </button>
                    </div>
                    {/* Show All Toggle (for list view) */}
                    {viewMode === 'list' && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-xs h-7"
                        onClick={() => setShowAllPayments(!showAllPayments)}
                      >
                        {showAllPayments ? 'Show Last 10' : `Show All (${paymentSummary.total_count})`}
                      </Button>
                    )}
                  </div>
                </div>
                
                {/* Bulk Payments View */}
                {viewMode === 'bulk' && (
                  <div className="space-y-4 max-h-[500px] overflow-y-auto">
                    {/* Bulk Payments */}
                    {paymentSummary.bulk_payments?.length > 0 ? (
                      paymentSummary.bulk_payments.map((bp, idx) => (
                        <div key={bp.bulk_payment_id} className="bg-white rounded-lg border overflow-hidden">
                          {/* Bulk Payment Header */}
                          <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-4 py-3 border-b">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-semibold text-sm text-foreground">
                                  Payment Received - ₹{bp.total_amount?.toLocaleString()}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {new Date(bp.payment_date).toLocaleDateString('en-IN', {day: '2-digit', month: 'short', year: '2-digit'})}
                                  {bp.payment_reference && ` • Ref: ${bp.payment_reference}`}
                                  {bp.payment_mode && ` • ${bp.payment_mode.toUpperCase()}`}
                                </p>
                                <p className="text-xs text-muted-foreground mt-1">
                                  {bp.customer_name} {bp.customer_code && `(${bp.customer_code})`}
                                </p>
                              </div>
                              <div className="text-right">
                                <Badge variant="outline" className="bg-green-100 text-green-800 border-green-300">
                                  {bp.invoice_adjustments?.length || 0} Invoice{(bp.invoice_adjustments?.length || 0) !== 1 ? 's' : ''}
                                </Badge>
                              </div>
                            </div>
                          </div>
                          {/* Invoice Adjustments */}
                          <div className="p-3 bg-gray-50">
                            <p className="text-xs font-semibold text-muted-foreground mb-2">Invoice Adjustments:</p>
                            <table className="w-full text-xs">
                              <thead className="bg-white">
                                <tr>
                                  <th className="text-left p-2 font-medium">Invoice</th>
                                  <th className="text-left p-2 font-medium">Division</th>
                                  <th className="text-right p-2 font-medium">Inv Value</th>
                                  <th className="text-right p-2 font-medium">CD Amount</th>
                                  <th className="text-right p-2 font-medium">Allocated</th>
                                  <th className="text-right p-2 font-medium">Short Bal</th>
                                </tr>
                              </thead>
                              <tbody>
                                {bp.invoice_adjustments?.map((adj, i) => (
                                  <tr key={i} className={`border-t border-border/30 ${adj.is_short_payment ? 'bg-purple-50' : 'bg-white'}`}>
                                    <td className="p-2 font-medium">
                                      {adj.invoice_number}
                                      {adj.is_short_payment && <span className="ml-1 text-purple-600 text-[10px]">●</span>}
                                    </td>
                                    <td className="p-2 text-muted-foreground">{adj.division || '-'}</td>
                                    <td className="p-2 text-right text-gray-700">
                                      {adj.invoice_amount > 0 ? `₹${adj.invoice_amount.toLocaleString()}` : '-'}
                                    </td>
                                    <td className="p-2 text-right text-blue-600">
                                      {adj.cd_amount > 0 ? `₹${adj.cd_amount.toLocaleString()}` : '-'}
                                    </td>
                                    <td className="p-2 text-right font-medium text-green-600">
                                      ₹{adj.allocated_amount?.toLocaleString()}
                                    </td>
                                    <td className="p-2 text-right font-medium text-purple-600">
                                      {adj.short_payment_balance > 0 ? `₹${adj.short_payment_balance.toLocaleString()}` : '-'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot className="bg-green-50/50">
                                <tr className="border-t-2 border-green-200">
                                  <td colSpan="2" className="p-2 font-semibold text-green-800">Total</td>
                                  <td className="p-2 text-right font-semibold text-gray-700">
                                    ₹{bp.invoice_adjustments?.reduce((sum, a) => sum + (a.invoice_amount || 0), 0).toLocaleString()}
                                  </td>
                                  <td className="p-2 text-right font-semibold text-blue-600">
                                    ₹{bp.invoice_adjustments?.reduce((sum, a) => sum + (a.cd_amount || 0), 0).toLocaleString()}
                                  </td>
                                  <td className="p-2 text-right font-semibold text-green-700">
                                    ₹{bp.invoice_adjustments?.reduce((sum, a) => sum + (a.allocated_amount || 0), 0).toLocaleString()}
                                  </td>
                                  <td className="p-2 text-right font-semibold text-purple-700">
                                    ₹{bp.invoice_adjustments?.reduce((sum, a) => sum + (a.short_payment_balance || 0), 0).toLocaleString()}
                                  </td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <p>No bulk payments found for the selected period.</p>
                        <p className="text-xs mt-1">Bulk payments are created when paying multiple invoices at once.</p>
                      </div>
                    )}
                    
                    {/* Individual Payments (not part of bulk) */}
                    {paymentSummary.individual_payments?.length > 0 && (
                      <div className="mt-4">
                        <p className="text-xs font-semibold text-muted-foreground mb-2">
                          Individual Payments ({paymentSummary.individual_payments.length})
                        </p>
                        <div className="bg-white rounded-lg border overflow-hidden">
                          <table className="w-full text-xs">
                            <thead className="bg-muted/30">
                              <tr>
                                <th className="text-left p-2 font-medium">Date</th>
                                <th className="text-left p-2 font-medium">Customer</th>
                                <th className="text-left p-2 font-medium">Invoice</th>
                                <th className="text-left p-2 font-medium">Reference</th>
                                <th className="text-right p-2 font-medium">Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {paymentSummary.individual_payments.slice(0, showAllPayments ? undefined : 10).map((p, i) => (
                                <tr key={i} className="border-t border-border/30 hover:bg-muted/20">
                                  <td className="p-2">{new Date(p.payment_date).toLocaleDateString('en-IN', {day: '2-digit', month: 'short'})}</td>
                                  <td className="p-2">{p.customer_name}</td>
                                  <td className="p-2 font-medium">{p.invoice_number}</td>
                                  <td className="p-2 text-muted-foreground truncate max-w-[100px]">{p.payment_reference || '-'}</td>
                                  <td className="p-2 text-right font-medium text-green-600">₹{p.payment_amount.toLocaleString()}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Grouped View - By Customer */}
                {viewMode === 'grouped' && paymentSummary.customer_summary && (
                  <div className="space-y-4 max-h-[500px] overflow-y-auto">
                    {paymentSummary.customer_summary.map((cust, idx) => (
                      <div key={cust.customer_id} className="bg-white rounded-lg border overflow-hidden">
                        {/* Customer Header */}
                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-3 border-b">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-semibold text-sm text-foreground">{cust.customer_name}</p>
                              <p className="text-xs text-muted-foreground">
                                {cust.customer_code && `Code: ${cust.customer_code} • `}
                                {cust.region}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-lg font-bold text-green-600">₹{cust.total_amount.toLocaleString()}</p>
                              <p className="text-xs text-muted-foreground">
                                {cust.payments.length} payment{cust.payments.length > 1 ? 's' : ''} • 
                                {cust.first_date && cust.last_date && (
                                  cust.first_date === cust.last_date 
                                    ? ` ${new Date(cust.first_date).toLocaleDateString('en-IN', {day: '2-digit', month: 'short'})}`
                                    : ` ${new Date(cust.first_date).toLocaleDateString('en-IN', {day: '2-digit', month: 'short'})} - ${new Date(cust.last_date).toLocaleDateString('en-IN', {day: '2-digit', month: 'short'})}`
                                )}
                              </p>
                            </div>
                          </div>
                        </div>
                        {/* Customer Payments Table */}
                        <table className="w-full text-xs">
                          <thead className="bg-muted/30">
                            <tr>
                              <th className="text-left p-2 font-medium">Date</th>
                              <th className="text-left p-2 font-medium">Invoice</th>
                              <th className="text-left p-2 font-medium">Mode</th>
                              <th className="text-left p-2 font-medium">Reference</th>
                              <th className="text-right p-2 font-medium">Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {cust.payments.map((p, i) => (
                              <tr key={i} className="border-t border-border/30 hover:bg-muted/20">
                                <td className="p-2">{new Date(p.payment_date).toLocaleDateString('en-IN', {day: '2-digit', month: 'short'})}</td>
                                <td className="p-2 font-medium">{p.invoice_number}</td>
                                <td className="p-2 uppercase text-muted-foreground">{p.payment_mode}</td>
                                <td className="p-2 text-muted-foreground truncate max-w-[100px]">{p.payment_reference || '-'}</td>
                                <td className="p-2 text-right font-medium text-green-600">₹{p.payment_amount.toLocaleString()}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot className="bg-green-50/50">
                            <tr className="border-t-2 border-green-200">
                              <td colSpan="4" className="p-2 font-semibold text-green-800">
                                Customer Total
                              </td>
                              <td className="p-2 text-right font-bold text-green-700">
                                ₹{cust.total_amount.toLocaleString()}
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* List View - All Payments */}
                {viewMode === 'list' && (
                  <div className="bg-white rounded-lg border overflow-hidden">
                    <div className="max-h-[400px] overflow-y-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-muted/50 sticky top-0">
                          <tr>
                            <th className="text-left p-2 font-medium">Date</th>
                            <th className="text-left p-2 font-medium">Customer</th>
                            <th className="text-left p-2 font-medium">Invoice</th>
                            <th className="text-left p-2 font-medium">Mode</th>
                            <th className="text-right p-2 font-medium">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(showAllPayments ? paymentSummary.payments : paymentSummary.payments.slice(0, 10)).map((p, i) => (
                            <tr key={i} className="border-t border-border/50 hover:bg-muted/30">
                              <td className="p-2">{new Date(p.payment_date).toLocaleDateString('en-IN')}</td>
                              <td className="p-2 truncate max-w-[150px]">{p.customer_name}</td>
                              <td className="p-2">{p.invoice_number}</td>
                              <td className="p-2 uppercase text-muted-foreground">{p.payment_mode}</td>
                              <td className="p-2 text-right font-medium text-green-600">₹{p.payment_amount.toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {/* Grand Total Footer */}
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-4 py-3 border-t-2 border-green-200">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-green-800">
                          Grand Total ({showAllPayments ? paymentSummary.total_count : Math.min(10, paymentSummary.total_count)} payments)
                        </span>
                        <span className="text-lg font-bold text-green-700">
                          ₹{(showAllPayments 
                            ? paymentSummary.total_amount 
                            : paymentSummary.payments.slice(0, 10).reduce((sum, p) => sum + p.payment_amount, 0)
                          ).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}
      
      {/* Ageing Buckets */}
      <Card data-testid="ageing-buckets" className="border-border/60 shadow-sm">
        <CardHeader>
          <CardTitle className="text-2xl font-heading font-semibold flex items-center gap-2">
            <AlertTriangle className="text-amber-600" size={24} />
            Payment Ageing Analysis
          </CardTitle>
        </CardHeader>
        <CardContent>
          {ageingData.summary && (
            <div className="mb-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="p-3 bg-gray-50 rounded-lg text-center border border-border/50">
                  <p className="text-lg font-bold">₹{(ageingData.summary.gross_outstanding || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</p>
                  <p className="text-[11px] text-muted-foreground">Gross Outstanding (aged debits)</p>
                </div>
                <div className="p-3 bg-emerald-50 rounded-lg text-center border border-emerald-200">
                  <p className="text-lg font-bold text-emerald-700">− ₹{(ageingData.summary.unadjusted_credits || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</p>
                  <p className="text-[11px] text-muted-foreground">Unadjusted Credits (payments · GR · CN)</p>
                </div>
                <div className="p-3 bg-indigo-50 rounded-lg text-center border border-indigo-200">
                  <p className="text-lg font-bold text-indigo-700">₹{(ageingData.summary.net_outstanding || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</p>
                  <p className="text-[11px] text-muted-foreground">Net Outstanding (ties to ledger balance)</p>
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground mt-2">
                Buckets age gross open debits by invoice date. Net = gross − unadjusted credit-side entries
                {ageingData.summary.credits_by_type && Object.keys(ageingData.summary.credits_by_type).length > 0 && (
                  <> ({Object.entries(ageingData.summary.credits_by_type).map(([t, v]) => `${t.replace(/_/g, ' ')}: ₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`).join(' · ')})</>
                )}.
              </p>
            </div>
          )}
          <Tabs defaultValue="0-15" className="w-full">
            <TabsList className="grid w-full grid-cols-5 mb-6">
              <TabsTrigger data-testid="tab-0-15" value="0-15" className="text-green-700">
                0-15 Days ({ageingData['0-15'].length})
              </TabsTrigger>
              <TabsTrigger data-testid="tab-16-45" value="16-45" className="text-blue-700">
                16-45 Days ({ageingData['16-45'].length})
              </TabsTrigger>
              <TabsTrigger data-testid="tab-46-90" value="46-90" className="text-yellow-700">
                46-90 Days ({ageingData['46-90'].length})
              </TabsTrigger>
              <TabsTrigger data-testid="tab-91-120" value="91-120" className="text-orange-700">
                91-120 Days ({ageingData['91-120'].length})
              </TabsTrigger>
              <TabsTrigger data-testid="tab-121-plus" value="121+" className="text-red-700">
                121+ Days ({ageingData['121+'].length})
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="0-15">
              {renderAgeingTable(ageingData['0-15'], '0-15')}
            </TabsContent>
            
            <TabsContent value="16-45">
              {renderAgeingTable(ageingData['16-45'], '16-45')}
            </TabsContent>
            
            <TabsContent value="46-90">
              {renderAgeingTable(ageingData['46-90'], '46-90')}
            </TabsContent>
            
            <TabsContent value="91-120">
              {renderAgeingTable(ageingData['91-120'], '91-120')}
            </TabsContent>
            
            <TabsContent value="121+">
              {renderAgeingTable(ageingData['121+'], '121+')}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
      
      {/* Payment Recording Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="sm:max-w-[550px] max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-xl font-heading font-semibold">Record Payment</DialogTitle>
            <DialogDescription>
              Invoice: {selectedInvoice?.invoice_number}
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-3 pr-2">
          {selectedInvoice && (
            <div className="p-3 bg-muted rounded-lg">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-muted-foreground">Customer:</span>
                  <span className="ml-2 font-medium">{selectedInvoice.customer_name}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Balance:</span>
                  <span className="ml-2 font-medium">₹{selectedInvoice.amount.toLocaleString()}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Days:</span>
                  <span className="ml-2 font-medium">{selectedInvoice.days_from_invoice}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Interest:</span>
                  <span className="ml-2 font-medium text-amber-600">₹{(selectedInvoice.interest_amount || 0).toLocaleString()}</span>
                </div>
              </div>
            </div>
          )}
          
          <div className="space-y-3">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold">Payments</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="rounded-full text-xs"
                  onClick={() => setPaymentForm(prev => ({
                    ...prev,
                    payments: [...prev.payments, {
                      payment_amount: '',
                      payment_date: new Date().toISOString().split('T')[0],
                      payment_mode: 'neft',
                      payment_reference: '',
                      remarks: ''
                    }]
                  }))}
                >
                  + Add payment
                </Button>
              </div>

              {paymentForm.payments.map((p, idx) => (
                <div key={idx} className="p-4 border rounded-lg space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-sm">Payment Amount *</Label>
                      <Input
                        type="number"
                        value={p.payment_amount}
                        onChange={(e) => {
                          const v = e.target.value;
                          setPaymentForm(prev => {
                            const payments = prev.payments.slice();
                            payments[idx] = { ...payments[idx], payment_amount: v };
                            return { ...prev, payments };
                          });
                        }}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Payment Date *</Label>
                      <Input
                        type="date"
                        value={p.payment_date}
                        onChange={(e) => {
                          const v = e.target.value;
                          setPaymentForm(prev => {
                            const payments = prev.payments.slice();
                            payments[idx] = { ...payments[idx], payment_date: v };
                            return { ...prev, payments };
                          });
                        }}
                        className="mt-1"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-sm">Payment Mode *</Label>
                      <Select
                        value={p.payment_mode}
                        onValueChange={(value) => {
                          setPaymentForm(prev => {
                            const payments = prev.payments.slice();
                            payments[idx] = { ...payments[idx], payment_mode: value };
                            return { ...prev, payments };
                          });
                        }}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="neft">NEFT</SelectItem>
                          <SelectItem value="rtgs">RTGS</SelectItem>
                          <SelectItem value="cheque">Cheque</SelectItem>
                          <SelectItem value="upi">UPI</SelectItem>
                          <SelectItem value="cash">Cash</SelectItem>
                          <SelectItem value="imps">IMPS</SelectItem>
                          <SelectItem value="dd">Demand Draft</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-sm">Reference Number</Label>
                      <Input
                        placeholder="Cheque number or transaction ID"
                        value={p.payment_reference}
                        onChange={(e) => {
                          const v = e.target.value;
                          setPaymentForm(prev => {
                            const payments = prev.payments.slice();
                            payments[idx] = { ...payments[idx], payment_reference: v };
                            return { ...prev, payments };


                          });
                        }}
                        className="mt-1"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-sm">Remarks</Label>
                    <Input
                      placeholder="Notes for this payment"
                      value={p.remarks}
                      onChange={(e) => {
                        const v = e.target.value;
                        setPaymentForm(prev => {
                          const payments = prev.payments.slice();
                          payments[idx] = { ...payments[idx], remarks: v };
                          return { ...prev, payments };
                        });
                      }}
                      className="mt-1"
                    />
                  </div>

                  {paymentForm.payments.length > 1 && (
                    <div className="flex justify-end">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-destructive"
                        onClick={() => setPaymentForm(prev => ({
                          ...prev,
                          payments: prev.payments.filter((_, i) => i !== idx)
                        }))}
                      >
                        Remove
                      </Button>
                    </div>
                  )}
                </div>
              ))}


            {/* Live remaining preview */}
            {selectedInvoice && (
              <div className="p-4 border rounded-lg bg-blue-50 border-blue-200">
                {(() => {
                  const netPayable = (selectedInvoice.amount || 0) - (selectedInvoice.cash_discount_amount || 0);
                  const alreadyPaid = (selectedInvoice.paid_amount || 0);
                  const remainingBefore = Math.max(netPayable - alreadyPaid, 0);

                  const creditsSelectedTotal = selectedCredits.reduce((sum, c) => sum + (Number(c.amount) || 0), 0);
                  const creditsAppliedPreview = Math.min(remainingBefore, creditsSelectedTotal);

                  const paymentsEntered = paymentForm.payments.reduce((sum, p) => sum + (Number(p.payment_amount) || 0), 0);

                  const remainingAfter = Math.max(remainingBefore - creditsAppliedPreview - paymentsEntered, 0);

                  return (
                    <div className="text-sm text-blue-900 space-y-1">
                      <div className="flex justify-between">
                        <span className="font-medium">Net payable (after CD)</span>
                        <span>₹{netPayable.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Already paid</span>
                        <span>₹{alreadyPaid.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Remaining before this entry</span>
                        <span className="font-semibold">₹{remainingBefore.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Credits selected (applied)</span>
                        <span>₹{creditsAppliedPreview.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Payments entered</span>
                        <span>₹{paymentsEntered.toLocaleString()}</span>
                      </div>
                      <div className="pt-2 border-t border-blue-200 flex justify-between">
                        <span className="font-semibold">Remaining after credits + payments</span>
                        <span className="font-bold">₹{remainingAfter.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between pt-1">
                        <span className="font-semibold">Expected to close invoice</span>
                        <span className={remainingAfter === 0 ? "font-bold text-emerald-700" : "font-bold text-amber-700"}>
                          {remainingAfter === 0 ? "Yes" : "No"}
                        </span>
                      </div>

                      {(() => {
                        if (paymentForm.cd_policy === 'no_cd') {
                          return (
                            <div className="flex justify-between pt-1">
                              <span className="font-semibold">Will close under CD policy</span>
                              <span className="font-bold text-muted-foreground">N/A</span>
                            </div>
                          );
                        }

                        const due = selectedInvoice.due_date ? new Date(selectedInvoice.due_date) : null;
                        const hasAnyPaymentDate = paymentForm.payments.some(p => !!p.payment_date);
                        if (!hasAnyPaymentDate || !due) {
                          return (
                            <div className="flex justify-between pt-1">
                              <span className="font-semibold">Will close under CD policy</span>
                              <span className="font-bold text-muted-foreground">N/A (needs a payment date)</span>
                            </div>
                          );
                        }

                        const lastPaymentDate = paymentForm.payments
                          .map(p => p.payment_date)
                          .filter(Boolean)
                          .map(d => new Date(d))
                          .sort((a,b) => b - a)[0];

                        const graceDue = new Date(due.getTime() + 5 * 24 * 60 * 60 * 1000);
                        const cdEligible = paymentForm.close_cd_after_management_approval || (lastPaymentDate <= graceDue);
                        const cdWillClose = remainingAfter === 0 && cdEligible;

                        return (
                          <div className="flex justify-between pt-1">
                            <span className="font-semibold">Will close under CD policy</span>
                            <span className={cdWillClose ? "font-bold text-emerald-700" : "font-bold text-amber-700"}>
                              {cdWillClose ? "Yes" : "No"}
                            </span>
                          </div>
                        );
                      })()}
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Adjust Credit Balances */}
            <div className="p-4 border rounded-lg bg-muted/30">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="font-semibold">Adjust Credit Balances</div>
                  <div className="text-xs text-muted-foreground">
                    Select one or more credit balances to adjust against this invoice.
                  </div>
                </div>
                <Button type="button" variant="ghost" size="sm" onClick={() => setSelectedCredits([])}>
                  Clear
                </Button>
              </div>

              {availableCredits.length === 0 ? (
                <div className="text-sm text-muted-foreground">No available credits for this customer.</div>
              ) : (
                <div className="space-y-2">
                  <Select
                    value=""
                    onValueChange={(value) => {
                      const selected = availableCredits.find((c) => c.transaction_id === value);
                      if (!selected) return;
                      if (selectedCredits.some((c) => c.transaction_id === selected.transaction_id)) return;

                      const labelParts = [
                        selected.reference ? `Ref: ${selected.reference}` : `Txn: ${selected.transaction_id}`,
                        `Amt: ₹${(selected.available_amount || selected.total_amount || 0).toLocaleString()}`,
                        selected.reason ? `Reason: ${selected.reason}` : null,
                        selected.remarks ? `Remarks: ${selected.remarks}` : null,
                      ].filter(Boolean);

                      setSelectedCredits((prev) => ([
                        ...prev,
                        {
                          transaction_id: selected.transaction_id,
                          label: labelParts.join(' • '),
                          amount: selected.available_amount || selected.total_amount || 0
                        }
                      ]));
                    }}
                  >
                    <SelectTrigger className="h-10">
                      <SelectValue placeholder="Add a credit balance to adjust" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableCredits.map((c) => (
                        <SelectItem key={c.transaction_id} value={c.transaction_id}>
                          {(c.reference ? `Ref: ${c.reference}` : c.transaction_id)}
                          {` • ₹${(c.available_amount || c.total_amount || 0).toLocaleString()}`}
                          {c.reason ? ` • ${c.reason}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {selectedCredits.length > 0 && (
                    <div className="space-y-2">
                      {selectedCredits.map((c) => (
                        <div key={c.transaction_id} className="flex items-center justify-between gap-3 p-2 rounded border bg-white">
                          <div className="text-xs text-muted-foreground truncate">{c.label}</div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-destructive"
                            onClick={() => setSelectedCredits((prev) => prev.filter((x) => x.transaction_id !== c.transaction_id))}
                          >
                            Remove
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}

                  {selectedCredits.length > 0 && (
                    <div className="text-xs text-muted-foreground">
                      Selected credits will be applied to this invoice before computing remaining balance.
                    </div>
                  )}
                </div>
              )}
            </div>

            </div>
            
            <div>
              <Label htmlFor="cd_policy">Cash Discount Policy *</Label>
              <Select
                value={paymentForm.cd_policy}
                onValueChange={(value) => setPaymentForm({...paymentForm, cd_policy: value})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cd_4_15">4% CD before GST in 15 days</SelectItem>
                  <SelectItem value="cd_3_30">3% CD before GST in 30 days</SelectItem>
                  <SelectItem value="cd_3_15">3% CD before GST in 15 days</SelectItem>
                  <SelectItem value="no_cd">No CD - Regular credit period</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {paymentForm.cd_policy !== 'no_cd' && selectedInvoice && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm font-semibold text-green-800 mb-2">
                  💰 Selected Policy: {
                    paymentForm.cd_policy === 'cd_4_15' ? '4% CD before GST in 15 days' :
                    paymentForm.cd_policy === 'cd_3_30' ? '3% CD before GST in 30 days' :
                    '3% CD before GST in 15 days'
                  }
                </p>
                <div className="text-sm text-green-700 space-y-1">
                  <p><strong>Amount before GST (5%):</strong> ₹{(selectedInvoice.amount / 1.05).toFixed(0).toLocaleString()}</p>
                  <p><strong>CD Amount:</strong> ₹{(
                    (selectedInvoice.amount / 1.05) * 
                    (paymentForm.cd_policy === 'cd_4_15' ? 0.04 : 0.03)
                  ).toFixed(0).toLocaleString()}</p>
                  <p className="font-bold text-green-900"><strong>Net Payable:</strong> ₹{(
                    selectedInvoice.amount - 
                    (selectedInvoice.amount / 1.05) * 
                    (paymentForm.cd_policy === 'cd_4_15' ? 0.04 : 0.03)
                  ).toFixed(0).toLocaleString()}</p>
                  <p className="text-xs mt-2 text-amber-700">
                    ⚠️ Payment must be within {paymentForm.cd_policy === 'cd_3_30' ? '30' : '15'} days of invoice date to qualify
                  </p>
                </div>
              </div>
            )}
          </div>

            {(user?.role === 'admin' || user?.role === 'accounts') && (
              <div className="p-3 border rounded-lg bg-amber-50 border-amber-200">
                <label className="flex items-center gap-2 text-xs font-medium text-amber-900">
                  <input
                    type="checkbox"
                    checked={paymentForm.close_cd_after_management_approval}
                    onChange={(e) => setPaymentForm(prev => ({ ...prev, close_cd_after_management_approval: e.target.checked }))}
                  />
                  Close CD after management approval
                </label>
              </div>
            )}
          </div>
          
          <DialogFooter className="flex-shrink-0 pt-4 border-t">
            <Button variant="outline" onClick={() => setPaymentDialogOpen(false)} size="sm">
              Cancel
            </Button>
            <Button onClick={handleRecordPayment} className="bg-green-600 hover:bg-green-700" size="sm" disabled={savingPayment}>
              <CheckCircle size={16} className="mr-1" />
              Record Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


